import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export interface PauseMenuProps {
  unread: number;
  isAdmin: boolean;
  onClose: () => void;
  onMessages: () => void;
  onCalendar: () => void;
  onAdmin: () => void;
  onSettings: () => void;
  onLeave: () => void;
}

/**
 * The game menu (Esc or the Menu button): resume, the office tools, and "Leave office" last,
 * set apart at the bottom. Arrow keys move between entries, Escape or a click outside closes it,
 * and focus returns to where it was.
 */
export function PauseMenu(props: PauseMenuProps) {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    listRef.current?.querySelector('button')?.focus();
    return () => opener?.focus();
  }, []);

  // Pick an entry: close the menu first, then run its action.
  const pick = (action: () => void) => () => {
    props.onClose();
    action();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    const buttons = [...(listRef.current?.querySelectorAll('button') ?? [])];
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const step = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    // Keep the arrow keys from also walking the avatar behind the menu.
    event.preventDefault();
    event.stopPropagation();
    buttons[(index + step + buttons.length) % buttons.length]?.focus();
  };

  return (
    <div className="overlay pause-overlay" onClick={props.onClose}>
      <div
        className="panel pause-menu"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pause-title"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <h1 id="pause-title">{t('hud.menu')}</h1>
        <div ref={listRef} className="pause-items" role="group">
          <button type="button" onClick={props.onClose}>
            {t('hud.resume')}
          </button>
          <button type="button" className="secondary" onClick={pick(props.onMessages)}>
            {t('dm.title')}
            {props.unread > 0 && <span className="badge">{props.unread}</span>}
          </button>
          <button type="button" className="secondary" onClick={pick(props.onCalendar)}>
            {t('calendar.open')}
          </button>
          {props.isAdmin && (
            <button type="button" className="secondary" onClick={pick(props.onAdmin)}>
              {t('lobby.admin')}
            </button>
          )}
          <button type="button" className="secondary" onClick={pick(props.onSettings)}>
            {t('settings.open')}
          </button>
          <button type="button" className="secondary pause-leave" onClick={pick(props.onLeave)}>
            {t('hud.leave')}
          </button>
        </div>
      </div>
    </div>
  );
}
